
import React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { Pencil } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useTranslationEdit } from "@/contexts/TranslationEditContext";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        // Add 24seven specific variants
        "24seven-primary": "bg-24seven-navy text-white hover:bg-24seven-hover",
        "24seven-secondary": "bg-white text-24seven-navy border border-24seven-border hover:bg-gray-50",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface TranslationButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  i18nKey: string;
  asChild?: boolean;
  values?: Record<string, string | number>;
}

const TranslationButton = React.forwardRef<HTMLButtonElement, TranslationButtonProps>(
  ({ i18nKey, className, variant, size, children, asChild = false, values, ...props }, ref) => {
    const { t } = useTranslation();
    const { isEditMode, setEditingKey } = useTranslationEdit();

    const handleClick = (e: React.MouseEvent<HTMLButtonElement | HTMLSpanElement>) => {
      console.log("TranslationButton clicked", i18nKey);

      if (isEditMode) {
        console.log("Edit mode is active");
        e.preventDefault();
        e.stopPropagation();
        setEditingKey(i18nKey);
        return;
      }

      if (props.onClick) {
        console.log("Calling onClick handler");
        props.onClick(e as React.MouseEvent<HTMLButtonElement>);
      }
    };

    const Comp = asChild ? Slot : "button";

    return (
      <Comp
        className={cn(
          buttonVariants({ variant, size, className }),
          isEditMode ? "translation-editable group" : ""
        )}
        ref={ref}
        onClick={handleClick}
        data-i18n-key={isEditMode ? i18nKey : undefined}
        type={props.type || "button"} // Set explicit type to avoid form submission in forms
        {...props}
      >
        {children || (values ? t(i18nKey, values) : t(i18nKey))}
        {isEditMode && (
          <span
            className="ml-1 inline-flex items-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
            onClick={(e) => {
              e.stopPropagation(); // Prevent the button's onClick from being triggered
              handleClick(e);
            }}
          >
            <Pencil className="h-3 w-3" />
          </span>
        )}
      </Comp>
    );
  }
);

TranslationButton.displayName = "TranslationButton";

export { TranslationButton, buttonVariants };
